/**
 * Legacy Master Job List (docs/08): search the old tickets, link one to a machine, and the
 * go-live cutover list. Legacy rows are read-only history; the server allows only the link and
 * the one-time cutover resolution, and logs both.
 */
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { openChoices, type Me } from '@wo/shared';
import { api, errorText, qs } from '../lib/api';
import { useSettings } from '../lib/hooks';
import type { CustomerRow, MachineRow } from '../lib/types';
import { Button, Card, ErrorText, Field, Input, Loading, Modal, PageHeader, Select, Table, Textarea } from '../components/ui';
import { can } from '../lib/perm';

export interface LegacyTicket {
  id: number;
  ticketNumber: number;
  customer: string | null;
  machine: string | null;
  description: string | null;
  dateOpened: string | null;
  dateClosed: string | null;
  notes: string | null;
  importFlags: string[] | null;
  linkedMachineId: number | null;
  resolutionWoId: number | null;
  cutoverClosedOn: string | null;
  linkedMachine?: { id: number; make: string; model: string; serialNumber: string } | null;
  resolutionWo?: { id: number; number: number } | null;
}

const FLAG_LABELS: Record<string, string> = { no_close_date: 'No close date', close_before_open: 'Closed before opened' };
export function Flags({ flags }: { flags: string[] | null }) {
  return (
    <span className="flex flex-wrap gap-1">
      {(flags ?? []).map((f) => (
        <span key={f} className="rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-900">
          {f.startsWith('duplicate_of:') ? `Same as #${f.split(':')[1]}` : (FLAG_LABELS[f] ?? f)}
        </span>
      ))}
    </span>
  );
}

function Status({ t }: { t: LegacyTicket }) {
  if (t.resolutionWo) return <Link className="text-blue-800 hover:underline" to={`/work-orders/${t.resolutionWo.id}`}>→ WO #{t.resolutionWo.number}</Link>;
  if (t.cutoverClosedOn) return <span>Closed {t.cutoverClosedOn} (cutover)</span>;
  return <span>{t.dateClosed ?? '—'}</span>;
}

export function LegacyTable({ rows, action }: { rows: LegacyTicket[]; action?: (t: LegacyTicket) => ReactNode }) {
  return (
    <Table head={['Ticket', 'Customer', 'Machine', 'Description', 'Opened', 'Closed', 'Notes', 'Flags', ...(action ? [''] : [])]} empty={rows.length === 0}>
      {rows.map((t) => (
        <tr key={t.id} className="align-top hover:bg-slate-50">
          <td className="px-3 py-2 font-bold">{t.ticketNumber}</td>
          <td className="px-3 py-2">{t.customer}</td>
          <td className="px-3 py-2">
            {t.machine}
            {t.linkedMachine && (
              <Link className="block text-xs text-blue-800 hover:underline" to={`/machines/${t.linkedMachine.id}`}>
                {t.linkedMachine.make} {t.linkedMachine.model} · {t.linkedMachine.serialNumber}
              </Link>
            )}
          </td>
          <td className="max-w-xs px-3 py-2">{t.description}</td>
          <td className="whitespace-nowrap px-3 py-2">{t.dateOpened}</td>
          <td className="whitespace-nowrap px-3 py-2"><Status t={t} /></td>
          <td className="max-w-xs px-3 py-2 text-slate-600">{t.notes}</td>
          <td className="px-3 py-2"><Flags flags={t.importFlags} /></td>
          {action && <td className="whitespace-nowrap px-3 py-2">{action(t)}</td>}
        </tr>
      ))}
    </Table>
  );
}

export function LegacyTicketsPage() {
  const [q, setQ] = useState('');
  const rows = useQuery({ queryKey: ['legacy', q], queryFn: () => api.get<LegacyTicket[]>(`/api/legacy-tickets${qs({ q, limit: 300 })}`) });
  return (
    <>
      <PageHeader title="Legacy tickets (Master Job List)">
        <Input placeholder="Ticket #, customer, machine, description…" value={q} onChange={(e) => setQ(e.target.value)} className="w-80" />
        <Link to="/legacy/cutover"><Button variant="secondary">Cutover list</Button></Link>
      </PageHeader>
      <p className="mb-3 text-sm text-slate-600">Imported exactly as typed in the old spreadsheet. Read-only; new work uses work orders 35001 and up.</p>
      <div className="rounded-lg border border-slate-200 bg-white">{rows.isLoading ? <Loading /> : <LegacyTable rows={rows.data ?? []} />}</div>
    </>
  );
}

interface Cutover {
  total: number;
  noCloseDate: number;
  remaining: number;
  resolved: number;
  done: boolean;
  imported: boolean;
  tickets: LegacyTicket[];
}

export function CutoverPage({ me }: { me: Me }) {
  const qc = useQueryClient();
  const data = useQuery({ queryKey: ['cutover'], queryFn: () => api.get<Cutover>('/api/legacy-tickets/cutover') });
  const [closing, setClosing] = useState<LegacyTicket | null>(null);
  const [opening, setOpening] = useState<LegacyTicket | null>(null);
  const canResolve = can(me, 'jobs.review');
  const refresh = () => {
    setClosing(null);
    setOpening(null);
    qc.invalidateQueries({ queryKey: ['cutover'] });
    qc.invalidateQueries({ queryKey: ['legacy'] });
  };
  if (data.isLoading || !data.data) return <Loading />;
  const d = data.data;
  return (
    <>
      <PageHeader title="Go-live cutover" />
      {!d.imported ? (
        <Card><p className="text-slate-600">The Master Job List hasn&apos;t been imported yet. See docs/runbook.md → Legacy import.</p></Card>
      ) : d.done ? (
        <Card>
          <p className="text-2xl font-bold text-green-700">✓ Cutover complete</p>
          <p className="mt-2 text-slate-600">All {d.noCloseDate} legacy tickets that had no close date are resolved. Every open job now lives in this system.</p>
        </Card>
      ) : (
        <>
          <Card className="mb-4">
            <p className="text-lg"><span className="font-bold">{d.remaining}</span> of {d.noCloseDate} legacy tickets still have no close date ({d.resolved} done).</p>
            <p className="mt-1 text-sm text-slate-600">For each one: if the job is finished, record the day it closed. If it is still being worked, open a new work order for it — the new job keeps a link to the old ticket number. Go-live is complete when this list is empty.</p>
          </Card>
          <div className="rounded-lg border border-slate-200 bg-white">
            <LegacyTable
              rows={d.tickets}
              action={(t) =>
                canResolve ? (
                  <span className="flex gap-2">
                    <Button variant="secondary" onClick={() => setClosing(t)}>Record close date</Button>
                    <Button onClick={() => setOpening(t)}>Open work order</Button>
                  </span>
                ) : (
                  <span className="text-xs text-slate-500">Manager resolves</span>
                )
              }
            />
          </div>
        </>
      )}
      {closing && <CloseDialog t={closing} onClose={() => setClosing(null)} onDone={refresh} />}
      {opening && <OpenWoDialog t={opening} onClose={() => setOpening(null)} onDone={refresh} />}
    </>
  );
}

function CloseDialog({ t, onClose, onDone }: { t: LegacyTicket; onClose: () => void; onDone: () => void }) {
  const [closedOn, setClosedOn] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const save = async () => {
    try {
      await api.post(`/api/legacy-tickets/${t.ticketNumber}/close`, { closedOn, note: note || undefined });
      onDone();
    } catch (e) {
      setError(errorText(e));
    }
  };
  return (
    <Modal title={`Record close date · ticket ${t.ticketNumber}`} onClose={onClose} footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button onClick={save} disabled={!closedOn}>Save</Button></>}>
      <p className="text-sm text-slate-600">{t.customer} · {t.machine} · {t.description}</p>
      <Field label="Date the job was finished" required><Input type="date" value={closedOn} onChange={(e) => setClosedOn(e.target.value)} /></Field>
      <Field label="Note (optional)"><Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} /></Field>
      <p className="text-xs text-slate-500">This can be done once per ticket and is recorded in the audit log.</p>
      <ErrorText error={error} />
    </Modal>
  );
}

function OpenWoDialog({ t, onClose, onDone }: { t: LegacyTicket; onClose: () => void; onDone: () => void }) {
  const settings = useSettings();
  const [customerQ, setCustomerQ] = useState(t.customer ?? '');
  const [customer, setCustomer] = useState<CustomerRow | null>(null);
  const [machine, setMachine] = useState<MachineRow | null>(null);
  const [jobType, setJobType] = useState('');
  const [problem, setProblem] = useState(`Legacy ticket #${t.ticketNumber}: ${t.description ?? ''}`.trim());
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<{ id: number; number: number } | null>(null);
  const customers = useQuery({ queryKey: ['customers', customerQ], queryFn: () => api.get<CustomerRow[]>(`/api/customers${qs({ q: customerQ })}`), enabled: !customer && customerQ.trim().length >= 2 });
  const machines = useQuery({ queryKey: ['machines', customer?.id], queryFn: () => api.get<MachineRow[]>(`/api/machines${qs({ customerId: customer!.id })}`), enabled: Boolean(customer) });

  const save = async () => {
    try {
      const r = await api.post<{ workOrder: { id: number; number: number } }>(`/api/legacy-tickets/${t.ticketNumber}/open-work-order`, {
        customerId: customer!.id,
        machineId: machine!.id,
        jobType,
        problemDescription: problem,
      });
      setCreated(r.workOrder);
    } catch (e) {
      setError(errorText(e));
    }
  };

  if (created) {
    return (
      <Modal title="Work order created" onClose={onDone} footer={<Button onClick={onDone}>Done</Button>}>
        <p className="text-center text-5xl font-black text-blue-800">#{created.number}</p>
        <p className="text-center text-sm text-slate-600">Linked to legacy ticket {t.ticketNumber}. <Link className="text-blue-800 underline" to={`/work-orders/${created.id}`}>Open it</Link></p>
      </Modal>
    );
  }
  return (
    <Modal
      title={`New work order for legacy ticket ${t.ticketNumber}`}
      onClose={onClose}
      footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button onClick={save} disabled={!customer || !machine || !jobType || !problem.trim()}>Create work order</Button></>}
    >
      <p className="text-sm text-slate-600">Spreadsheet: {t.customer} · {t.machine} · opened {t.dateOpened}</p>
      {customer ? (
        <div className="flex items-center justify-between text-sm"><span className="font-semibold">{customer.name}</span><Button variant="ghost" onClick={() => { setCustomer(null); setMachine(null); }}>Change</Button></div>
      ) : (
        <Field label="Customer" required hint="Pick the customer record (add new customers on the Customers screen).">
          <Input value={customerQ} onChange={(e) => setCustomerQ(e.target.value)} />
          <ul className="mt-1 max-h-40 overflow-auto rounded border border-slate-200">
            {customers.data?.map((c) => <li key={c.id}><button type="button" className="w-full px-3 py-1.5 text-left text-sm hover:bg-slate-50" onClick={() => setCustomer(c)}>{c.name} {c.phone && <span className="text-slate-500">· {c.phone}</span>}</button></li>)}
          </ul>
        </Field>
      )}
      {customer && (
        <Field label="Machine" required>
          <Select value={machine?.id ?? ''} onChange={(e) => setMachine(machines.data?.find((m) => m.id === Number(e.target.value)) ?? null)}>
            <option value="">Choose…</option>
            {machines.data?.map((m) => <option key={m.id} value={m.id}>{m.make} {m.model} · {m.serialNumber}</option>)}
          </Select>
        </Field>
      )}
      <Field label="Job type" required>
        <Select value={jobType} onChange={(e) => setJobType(e.target.value)}>
          <option value="">Choose…</option>
          {settings.data && openChoices(settings.data.jobTypes, settings.data.retiredJobTypes).map((j) => <option key={j}>{j}</option>)}
        </Select>
      </Field>
      <Field label="Details of problem" required><Textarea rows={3} value={problem} onChange={(e) => setProblem(e.target.value)} /></Field>
      <ErrorText error={error} />
    </Modal>
  );
}

/** Machines screen: legacy tickets linked to this machine + Link Legacy Ticket. */
export function MachineLegacyCard({ machineId }: { machineId: number }) {
  const qc = useQueryClient();
  const linked = useQuery({ queryKey: ['legacy', 'machine', machineId], queryFn: () => api.get<LegacyTicket[]>(`/api/legacy-tickets${qs({ machineId })}`) });
  const [linking, setLinking] = useState(false);
  const [q, setQ] = useState('');
  const [error, setError] = useState<string | null>(null);
  const found = useQuery({ queryKey: ['legacy', q], queryFn: () => api.get<LegacyTicket[]>(`/api/legacy-tickets${qs({ q, limit: 30 })}`), enabled: linking && q.trim().length >= 1 });
  const link = async (n: number, id: number | null) => {
    setError(null);
    try {
      await api.post(`/api/legacy-tickets/${n}/link`, { machineId: id });
      qc.invalidateQueries({ queryKey: ['legacy'] });
      setLinking(false);
    } catch (e) {
      setError(errorText(e));
    }
  };
  return (
    <Card title="Legacy tickets" actions={<Button variant="secondary" onClick={() => setLinking(true)}>Link Legacy Ticket</Button>}>
      {linked.data?.length ? (
        <ul className="space-y-2 text-sm">
          {linked.data.map((t) => (
            <li key={t.id} className="flex items-start justify-between gap-2">
              <span><span className="font-bold">#{t.ticketNumber}</span> {t.dateOpened} · {t.description}</span>
              <Button variant="ghost" onClick={() => link(t.ticketNumber, null)}>Unlink</Button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-slate-500">No legacy tickets linked.</p>
      )}
      {linking && (
        <Modal title="Link Legacy Ticket" onClose={() => setLinking(false)}>
          <Field label="Search the Master Job List" hint="Ticket number, customer, model or description"><Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} /></Field>
          <ul className="max-h-72 space-y-1 overflow-auto">
            {found.data?.map((t) => (
              <li key={t.id} className="flex items-start justify-between gap-2 rounded border border-slate-200 px-3 py-2 text-sm">
                <span><span className="font-bold">#{t.ticketNumber}</span> {t.customer} · {t.machine} · {t.description} <span className="text-slate-500">{t.dateOpened}</span></span>
                {t.linkedMachineId === machineId ? <span className="text-xs text-green-700">Linked</span> : <Button onClick={() => link(t.ticketNumber, machineId)}>Link</Button>}
              </li>
            ))}
          </ul>
          <ErrorText error={error} />
        </Modal>
      )}
    </Card>
  );
}
